import React, { useState } from "react";
import { ShieldCheck, BookOpen, Award, Phone, Video, Smartphone } from "lucide-react";
import { sendOtp, verifyOtp } from "../../api/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { storeAuthData } from "../../api/storageService";
import { useNavigate } from "react-router-dom";

const Login = () => {
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [error, setError] = useState("");
    const [resendCooldown, setResendCooldown] = useState(0);
    const navigate = useNavigate();

    const handleSendOtp = async () => {
        if (!phone || phone.length !== 10) {
            toast.error("Enter valid 10 digit phone number");
            return;
        }

        try {
            setLoading(true);
            const res = await sendOtp(phone, "weblogin");

            if (res.status) {
                setOtpSent(true);
                toast.success("OTP sent successfully ✅");
                // Start cooldown timer
                setResendCooldown(30);
                const timer = setInterval(() => {
                    setResendCooldown((prev) => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                toast.error(res.message || "Failed to send OTP");
            }
        } catch (err) {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendCooldown > 0) {
            toast.info(`Please wait ${resendCooldown} seconds before resending`);
            return;
        }

        if (!phone || phone.length !== 10) {
            toast.error("Enter valid 10 digit phone number");
            return;
        }

        try {
            setLoading(true);
            const res = await sendOtp(phone, "weblogin");

            if (res.status) {
                toast.success("OTP resent successfully ✅");
                // Reset cooldown
                setResendCooldown(30);
                const timer = setInterval(() => {
                    setResendCooldown((prev) => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                toast.error(res.message || "Failed to resend OTP");
            }
        } catch (err) {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    // In Login.jsx, update handleVerifyOtp function
    const handleVerifyOtp = async () => {
        // Validate OTP - 4 digits
        if (!otp || otp.length !== 4) {
            toast.error("Enter valid 4-digit OTP");
            return;
        }

        try {
            setLoading(true);
            const res = await verifyOtp(phone, otp, "weblogin");

            if (res.status) {
                storeAuthData(res); // Store auth data
                toast.success("Login successful 🎉");
                navigate("/dashboard"); // Redirect to dashboard
            } else {
                toast.error("Invalid OTP");
            }
        } catch (err) {
            toast.error("OTP verification failed");
        } finally {
            setLoading(false);
        }
    };
    const handleNavigateToRegister = () => {
        navigate("/register");
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="custom-science custom-padding pt-80 pb-80 xl-screen-padding">
                <div className="w-full grid md:grid-cols-10 gap-12">
                    {/* Left Section - 6 columns */}
                    <div className="hidden md:block md:col-span-6">
                        <h1 className="text-lg sm:text-xl custom-line-height md:text-xl lg:text-4xl
 font-bold text-gray-900 leading-tight mb-4 font-lato font-bold">
                            Welcome Back to Nutrillion
                        </h1>
                        <p className="text-gray-500 mb-4 font-lato text-p">
                            Your complete nutrition, wellness, and learning journey in one place. </p>
                        <p className="text-gray-500 mb-4 font-lato text-p">
                            Sign in to access your courses, health programs, consultations, progress reports, personalized recommendations, certificates, and wellness resources.                        </p>
                        <p className="text-gray-500 mb-4 font-lato text-p">
                            Everything you need, all in one dashboard</p>

                        <div className="grid grid-cols-2 gap-4 mb-8 font-lato">
                            {[
                                { icon: <BookOpen size={18} />, title: "Personalized Health Programs", desc: "Access your customized nutrition and wellness plans." },
                                { icon: <Award size={18} />, title: "Expert Consultations", desc: "Manage appointments and connect with certified nutrition experts." },
                                { icon: <Video size={18} />, title: "Learning & Certifications", desc: "Continue courses, complete assessments, and earn certificates." },
                                { icon: <Smartphone size={18} />, title: "Progress Tracking", desc: "Monitor your health goals, achievements, and learning milestones." },
                            ].map((item, i) => (
                                <div key={i} className="bg-[#f8f9fa] border rounded-xl p-4 font-lato">
                                    <div className="text-green-600 mb-2 font-lato">{item.icon}</div>
                                    <h4 className="text-md font-semibold text-gray-800 font-lato">{item.title}</h4>
                                    <p className="text-sm text-gray-500 font-lato">{item.desc}</p>
                                </div>
                            ))}
                        </div>

                        {/* <div className="flex gap-4">
                            {["12k+", "4.9/5", "24/7"].map((val, i) => (
                                <div key={i} className="bg-white border rounded-xl px-5 py-3 text-left">
                                    <p className="font-bold text-xl text-gray-900">{val}</p>
                                    <span className="text-xs text-gray-500">
                                        {i === 0 ? "Learners" : i === 1 ? "Rating" : "Support"}
                                    </span>
                                </div>
                            ))}
                        </div> */}
                    </div>

                    {/* Right Section - 4 columns */}
                    <div className="flex items-center justify-center font-lato md:col-span-4">
                        <div className="w-full max-w-md bg-white border rounded-2xl p-8 shadow-sm">
                            {/* <div className="flex items-center gap-2 text-green-700 text-p-center font-semibold bg-green-50 px-3 py-1 rounded-full w-fit mb-4">
                                <Smartphone size={14} className="text-green-600" />
                                Welcome Back to Nutrillion
                            </div> */}

                            <h2 className="text-md sm:text-lg custom-line-height md:text-lg lg:text-3xl font-semibold text-gray-900 mb-2 font-bold text-center md:text-left">
                                Log in with phone and OTP
                            </h2>

                            <p className="text-p-center text-gray-500 mb-4 ">
                                Use your registered mobile number to receive a one-time password
                            </p>

                            <div className="text-center text-p-center text-gray-400 mb-4 relative">
                                <span className="bg-white px-2 relative z-10">
                                    Secure OTP-based sign in
                                </span>
                                <div className="absolute top-1/2 left-0 w-full h-px bg-gray-200"></div>
                            </div>

                            <div className="mb-3">
                                <p className="text-p-center text-gray-500 mb-1">Phone number</p>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Enter phone number"
                                        value={phone}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                                            setPhone(value);
                                        }}
                                        className="w-full px-5 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-p-center"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-green-600 pointer-events-none">
                                        <Phone size={18} />
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={handleSendOtp}
                                disabled={loading}
                                className="w-full py-3 rounded-full bg-gray-100 text-gray-700 text-sm font-semibold mb-4 flex items-center justify-center gap-2"
                            >
                                {loading ? "Sending..." : "Send OTP"}
                            </button>

                            <div className="mb-2 relative">
                                <div className="flex justify-between items-center mb-1">
                                    <p className="text-p-center text-gray-500">Enter OTP</p>
                                    <span
                                        onClick={handleResendOtp}
                                        className={`text-p-center cursor-pointer ${resendCooldown > 0
                                            ? "text-gray-400 cursor-not-allowed"
                                            : "text-green-600 hover:text-green-700 hover:underline"
                                            }`}
                                    >
                                        {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : "Resend code"}
                                    </span>
                                </div>
                                <div className="relative ">
                                    <input
                                        type="text"
                                        placeholder="Enter OTP"
                                        value={otp}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                                            setOtp(value);
                                        }}
                                        className="w-full px-5 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-p-center"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-green-600">
                                        <ShieldCheck size={18} />
                                    </span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center text-xs text-gray-400 mb-4">
                                {/* Space for optional elements */}
                            </div>

                            <button
                                onClick={handleVerifyOtp}
                                disabled={loading}
                                className="w-full bg-[#15803d] text-white py-3 rounded-xl font-extrabold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {loading ? "Verifying..." : "Verify & Log In →"}
                            </button>
                            {/* Replace the existing description section with this */}
                            <div className="pt-4 text-left">
                                <p className="text-md font-semibold text-gray-800 font-lato">
                                    Continue Your Wellness Journey    </p>
                                <p className="text-sm text-gray-500 font-lato">
                                    Resume courses, track your health goals, access expert guidance, and stay on top of your personalized nutrition plan.    </p>
                            </div>
                            {/* <div className="grid grid-cols-2 gap-3 mt-5 font-lato">
                                <div className="bg-[#f1f5f9] p-4 rounded-xl text-gray-600 border border-gray-100 shadow-sm hover:shadow-md transition">
                                    <p className="font-semibold text-gray-800 text-xs mb-1 whitespace-nowrap">
                                        Continue enrolled courses
                                    </p>
                                    <p className="text-[12px] text-gray-500 leading-relaxed">
                                        Resume modules, assignments, and reading lists instantly.
                                    </p>
                                </div>
                                <div className="bg-[#f1f5f9] p-4 rounded-xl text-gray-600 border border-gray-100 shadow-sm hover:shadow-md transition">
                                    <p className="font-semibold text-xs text-gray-800 mb-1">
                                        View earned certificates
                                    </p>
                                    <p className="text-[12px] text-gray-500 leading-relaxed">
                                        Keep all completions and credentials in one place.
                                    </p>
                                </div>
                            </div> */}

                            <p className="text-center text-sm font-bold mt-4 text-gray-500">
                                New to Nutrillion?{" "}
                                <span
                                    onClick={handleNavigateToRegister}
                                    className="text-green-600 font-bold cursor-pointer font-semibold hover:underline"
                                >
                                    Create an account
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;